/**
 * ProjectOverviewTab — working-surface Command Center.
 *
 * Health Strip → Stat Cards → This Week → Team / Actions / Documents.
 * Governance (phases/gates/exceptions) lives on the Governance tab.
 * Header "Governed" badge remains the only overview governance signal.
 * All stat counts computed from the same task list used for completion bar.
 * Failed task rollup must surface an error — never silent zeros (OV-1 A2).
 */

import React, { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CalendarClock,
  ClipboardList,
  Clock,
  UserX,
} from 'lucide-react';
import { useEffectiveRole } from '@/utils/access/useEffectiveRole';
import { useProjectContext } from '../layout/ProjectPageLayout';
import { EmptyState } from '@/components/ui/feedback/EmptyState';
import { ProjectOverviewCards } from '../components/ProjectOverviewCards';
import { ProjectOverviewThisWeek } from '../components/ProjectOverviewThisWeek';
import type { ProjectOverview } from '../model/projectOverview';
import { CompletionBar } from '@/features/work-management/components/CompletionBar';
import {
  UnavailablePanel,
  UnavailableValue,
} from '@/components/ui/feedback/Unavailable';

const healthBadge: Record<
  string,
  { label: string; color: string; bg: string; dot: string }
> = {
  HEALTHY: {
    label: 'On Track',
    color: 'text-green-700 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-950/30',
    dot: 'bg-green-500',
  },
  AT_RISK: {
    label: 'At Risk',
    color: 'text-amber-700 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    dot: 'bg-amber-500',
  },
  BLOCKED: {
    label: 'Blocked',
    color: 'text-red-700 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
    dot: 'bg-red-500',
  },
  // OVERVIEW-HEALTH-UI-1 / M8a — never green.
  NOT_ENOUGH_DATA: {
    label: 'Not enough data',
    color: 'text-slate-600 dark:text-slate-300',
    bg: 'bg-slate-100 dark:bg-slate-800',
    dot: 'bg-slate-400',
  },
  // DRAFT-TRUTH-1: a Draft is not started, so it is not rated — never a RAG.
  NOT_RATED: {
    label: 'Draft · not rated',
    color: 'text-slate-600 dark:text-slate-300',
    bg: 'bg-slate-100 dark:bg-slate-800',
    dot: 'bg-slate-400',
  },
};

/** Missing or unknown healthCode — never coerce to HEALTHY. */
const healthUnavailable = {
  label: 'Health unavailable',
  color: 'text-slate-600 dark:text-slate-300',
  bg: 'bg-slate-100 dark:bg-slate-800',
  dot: 'bg-slate-400',
};

function resolveHealthBadge(healthCode: string | null | undefined) {
  if (healthCode == null) return healthUnavailable;
  return healthBadge[healthCode] ?? healthUnavailable;
}

export const ProjectOverviewTab: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    project,
    overviewSnapshot,
    overviewLoading,
    refreshOverviewSnapshot,
    isReadOnly,
  } = useProjectContext();
  const effectiveWorkspaceId = project?.workspaceId ?? '';
  const { can } = useEffectiveRole();
  const canProjectEdit = can('project.edit');
  const canCreateDocuments = can('document.create');

  const overview: ProjectOverview | null = overviewSnapshot;

  // F42 (TR-2026-11-02-15): every task number on this tab comes from the
  // server's project-wide rollup (`overview.taskRollup`), never from a count
  // over the capped client task list. No rollup in the payload = the rollup
  // could not be read: Unavailable, never a zero.
  const rollup = overview?.taskRollup ?? null;
  const rollupUnavailable = rollup === null;
  // COUNTS-LEAF-1 (D-2 / D-85): every attention count comes from the server,
  // computed over the whole project with the project's own statuses — never a
  // browser count over a page of tasks. A missing count renders "—", not 0.
  // DRAFT-TRUTH-1: an ungated Draft counts nothing overdue — show "not counted",
  // never a green 0 (a suppressed count is not a measured zero).
  const overdueNotCounted = overview?.draft?.overdueCounted === false;
  const overdueTasks = overdueNotCounted ? null : (rollup?.overdueTasks ?? null);
  const dueThisWeekTasks = rollup?.dueThisWeekTasks ?? null;
  const unassignedTasks = rollup?.unassignedTasks ?? null;
  const totalTasks = rollup?.totalTasks ?? null;
  const doneTasks = rollup?.doneTasks ?? null;
  const openTasks = rollup?.openTasks ?? null;
  const cancelledTasks = rollup?.cancelledTasks ?? null;
  const completionPercent = rollup?.completionPercent ?? null;
  // "N done · M open", plus "· K canceled" only when something is canceled.
  // Never "remaining": done + remaining is not total once a task is cancelled.
  const totalSubtitle =
    doneTasks === null || openTasks === null
      ? 'done and open counts unavailable'
      : `${doneTasks} done · ${openTasks} open${
          cancelledTasks !== null && cancelledTasks > 0 ? ` · ${cancelledTasks} canceled` : ''
        }`;

  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (taskId) {
      navigate(`/projects/${projectId}/tasks?taskId=${taskId}`, { replace: true });
    }
  }, [projectId, searchParams, navigate]);

  // ── Loading / Error states ─────────────────────────────────────
  if (overviewLoading && !overview) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!overviewLoading && !overview) {
    return (
      <EmptyState
        title="Unable to load overview"
        description="Project overview could not be loaded. Try again."
        icon={<AlertCircle className="h-12 w-12" />}
        action={
          <button
            type="button"
            onClick={() => void refreshOverviewSnapshot()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Try Again
          </button>
        }
      />
    );
  }

  if (!overview) return null;

  const health = resolveHealthBadge(overview.healthCode);
  const dueWorkEvidence = overview.needsAttention.find(
    (item) => item.typeCode === 'DUE_WORK_BELOW_TOLERANCE' && item.reasonText,
  );

  return (
    <div className="space-y-5">
      {/* ── Health Strip ───────────────────────────────────────── */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-center gap-4">
          {/* Completion */}
          <div className="flex items-center gap-3 min-w-[200px] flex-1">
            {rollupUnavailable ? (
              <UnavailableValue testId="overview-completion-unavailable" className="text-sm" />
            ) : completionPercent === null ? (
              <span
                className="text-sm text-slate-500 dark:text-slate-400"
                data-testid="overview-completion-not-enough-data"
              >
                Not enough data
              </span>
            ) : (
              <CompletionBar percent={completionPercent} size="md" />
            )}
          </div>

          {/* Health badge */}
          <div
            className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${health.bg} ${health.color}`}
            data-testid="overview-health-badge"
            data-health-code={overview.healthCode ?? 'UNAVAILABLE'}
          >
            <span className={`h-2 w-2 rounded-full ${health.dot}`} />
            {health.label}
          </div>

          {/* Current phase */}
          {overview.projectState && (
            <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {overview.projectState}
            </span>
          )}

          {/* Methodology */}
          {project?.methodology && (
            <span className="rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 capitalize dark:bg-indigo-950/40 dark:text-indigo-300">
              {project.methodology}
            </span>
          )}
        </div>
        {overview.draft ? (
          <p
            className="mt-2 text-xs text-slate-600 dark:text-slate-300"
            data-testid="overview-draft-note"
          >
            {overview.draft.workUnderWay
              ? 'Draft · work under way'
              : 'Draft · not started · dates are planned'}
          </p>
        ) : null}
        {dueWorkEvidence ? (
          <p
            className="mt-2 text-xs text-amber-800 dark:text-amber-200"
            data-testid="overview-due-work-evidence"
          >
            {dueWorkEvidence.reasonText}
          </p>
        ) : null}
      </div>

      {/* ── Stat Cards ──────────────────────────────────────────── */}
      {rollupUnavailable ? (
        <div
          className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
          data-testid="overview-task-rollup-unavailable"
        >
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Task counts</p>
          <UnavailablePanel
            onRetry={() => void refreshOverviewSnapshot()}
            detail="The project's task totals could not be read, so none are shown as zero."
            testId="overview-task-rollup-unavailable-panel"
          />
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* Stat cards — 3 columns */}
        <StatCard
          icon={<ClipboardList className="h-5 w-5 text-indigo-500" />}
          value={totalTasks}
          label="Total Tasks"
          subtitle={totalSubtitle}
          description="Counts tasks without subtasks"
          testId="overview-total-tasks-count"
        />
        <StatCard
          icon={<Clock className="h-5 w-5 text-red-500" />}
          value={overdueTasks}
          label="Overdue"
          subtitle={
            overdueNotCounted
              ? 'not counted until the project starts'
              : 'tasks past due date'
          }
          valueColor={
            overdueNotCounted || typeof overdueTasks !== 'number'
              ? 'text-slate-500 dark:text-slate-400'
              : overdueTasks > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-green-600 dark:text-green-400'
          }
          notCounted={overdueNotCounted}
          testId="overview-overdue-count"
        />
        <StatCard
          icon={<CalendarClock className="h-5 w-5 text-blue-500" />}
          value={dueThisWeekTasks}
          label="Due this week"
          subtitle="due today through the next 6 days"
          testId="overview-due-this-week-count"
        />
        <StatCard
          icon={<UserX className="h-5 w-5 text-amber-500" />}
          value={unassignedTasks}
          label="Unassigned"
          subtitle="tasks without an owner"
          valueColor={
            typeof unassignedTasks !== 'number'
              ? 'text-slate-500 dark:text-slate-400'
              : unassignedTasks > 0
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-green-600 dark:text-green-400'
          }
          testId="overview-unassigned-count"
        />

        {/* Behind target (from overview API) */}
        {overview.behindTargetDays !== null && overview.behindTargetDays > 0 && (
          <StatCard
            icon={<AlertCircle className="h-5 w-5 text-red-500" />}
            value={overview.behindTargetDays}
            label="Days Behind"
            subtitle="behind target date"
            valueColor="text-red-600 dark:text-red-400"
          />
        )}
      </div>
      )}

      {/* Governance (phases/gates/exceptions) lives on the Governance tab. */}

      {projectId && effectiveWorkspaceId && (
        <ProjectOverviewThisWeek
          projectId={projectId}
          workspaceId={effectiveWorkspaceId}
        />
      )}

      {/* ── Existing cards (Team, Actions, Documents) ──── */}
      {project && effectiveWorkspaceId && (
        <ProjectOverviewCards
          project={project}
          workspaceId={effectiveWorkspaceId}
          overview={overview}
          taskCount={rollupUnavailable ? null : totalTasks}
          canEdit={canProjectEdit && !isReadOnly}
          canCreateDocuments={canCreateDocuments && !isReadOnly}
        />
      )}
    </div>
  );
};

/* ── Stat Card ──────────────────────────────────────────────────── */

function StatCard({
  icon,
  value,
  label,
  subtitle,
  valueColor = 'text-slate-900 dark:text-slate-100',
  testId,
  description,
  notCounted = false,
}: {
  icon: React.ReactNode;
  value: number | null;
  label: string;
  subtitle: string;
  valueColor?: string;
  testId?: string;
  /** One line explaining what the number counts; shown as a tooltip and read by screen readers. */
  description?: string;
  /** A named "not counted yet" state (DRAFT-TRUTH-1): rendered as a dash with its own subtitle, not as Unavailable. */
  notCounted?: boolean;
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
      title={description}
      aria-description={description}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-3xl font-bold ${valueColor}`} data-testid={testId}>
            {value !== null ? (
              value
            ) : notCounted ? (
              '—'
            ) : (
              <UnavailableValue className="text-xl font-semibold" testId={testId ? `${testId}-unavailable` : undefined} />
            )}
          </p>
          <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-400">{label}</p>
          <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-2 dark:bg-slate-700/50">{icon}</div>
      </div>
    </div>
  );
}

export default ProjectOverviewTab;
